/**
 * Where a boat may be planted at the start: anywhere legal in the start area, as long as its
 * drawn hull keeps `PLACEMENT_GAP_M` of water to every boat already planted. Humans and bots
 * plant under the same rule.
 */
import { metersToPixels } from './constants.ts';
import type { HullDimensions } from './hullSize.ts';
import { closestPointOnSegment, distance } from './map/document.ts';
import { fromAngle, segmentsIntersect } from './math.ts';
import type { Vec2 } from './types.ts';

/** Water between two planted hulls, in metres. */
export const PLACEMENT_GAP_M = 2;

export type PlacedHull = {
  position: Vec2;
  heading: number;
  dimensions: HullDimensions;
};

type Spine = { from: Vec2; to: Vec2; radius: number };

/** The drawn hull as a capsule: spine along the keel, half the beam around it. */
function spineOf(position: Vec2, heading: number, dimensions: HullDimensions): Spine {
  const half = metersToPixels(Math.max(dimensions.lengthM - dimensions.beamM, 0)) / 2;
  const along = fromAngle(heading, half);

  return {
    from: { x: position.x - along.x, y: position.y - along.y },
    to: { x: position.x + along.x, y: position.y + along.y },
    radius: metersToPixels(dimensions.beamM) / 2,
  };
}

function pointToSegment(point: Vec2, from: Vec2, to: Vec2): number {
  return distance(point, closestPointOnSegment(point, from, to));
}

function segmentGap(a: Spine, b: Spine): number {
  if (segmentsIntersect(a.from, a.to, b.from, b.to)) {
    return 0;
  }

  return Math.min(
    pointToSegment(a.from, b.from, b.to),
    pointToSegment(a.to, b.from, b.to),
    pointToSegment(b.from, a.from, a.to),
    pointToSegment(b.to, a.from, a.to),
  );
}

/**
 * Whether a hull planted at `position` keeps the gap to every planted hull. With `heading` null
 * (the plant click, before the bow is aimed) only the beam is checked, so a boat can still go
 * alongside; the aim then checks the whole hull.
 */
export function placementClear(
  placed: readonly PlacedHull[],
  position: Vec2,
  heading: number | null,
  dimensions: HullDimensions,
  gapM: number = PLACEMENT_GAP_M,
): boolean {
  const gap = metersToPixels(gapM);
  const halfBeam = metersToPixels(dimensions.beamM) / 2;

  return placed.every((other) => {
    const theirs = spineOf(other.position, other.heading, other.dimensions);

    if (heading === null) {
      return pointToSegment(position, theirs.from, theirs.to) >= theirs.radius + halfBeam + gap;
    }

    const mine = spineOf(position, heading, dimensions);
    return segmentGap(mine, theirs) >= theirs.radius + mine.radius + gap;
  });
}
